import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { AppHeader } from "../components/AppHeader";
import { Button } from "../components/ui/button";
import { KeyRound, Search, CalendarClock, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }) : "");

export default function Referrals() {
  const { user } = useAuth();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const { data, error } = await supabase
        .from("booking_tokens")
        .select("id,token,gp_clinic_name,created_at,clinician_id,clinicians!clinician_id(first_name,last_name,profession,specialty,suburb,state)")
        .order("created_at", { ascending: false });
      if (!active) return;
      if (error) toast.error("Could not load SimplyRefer References: " + error.message);
      setTokens(data || []);
      setLoading(false);
    };
    load();
    return () => { active = false; };
  }, [user]);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader firstName={user?.user_metadata?.first_name} />

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 animate-fadein" data-testid="referrals-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">My SimplyRefer References</h1>
            <p className="mt-1 text-sm text-slate-500">Optional references you have created, newest first. No patient information is stored.</p>
          </div>
          <span className="text-sm text-slate-500" data-testid="referrals-count">
            {loading ? "Loading…" : `${tokens.length} reference${tokens.length === 1 ? "" : "s"}`}
          </span>
        </div>

        {loading ? (
          <div className="mt-8 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-md border border-slate-200 bg-white" />
            ))}
          </div>
        ) : tokens.length === 0 ? (
          <div className="mt-10 rounded-md border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="referrals-empty">
            <KeyRound className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-medium text-slate-700">No SimplyRefer References yet</p>
            <p className="mt-1 text-sm text-slate-500">Choose a clinician, then select the referral option that suits your usual workflow.</p>
            <Button asChild className="mt-5" data-testid="referrals-find-clinician-button">
              <Link to="/clinicians"><Search className="mr-2 h-4 w-4" /> Find a clinician</Link>
            </Button>
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="referrals-list">
            {tokens.map((t) => {
              const c = t.clinicians;
              return (
                <div key={t.id} className="rounded-md border border-slate-200 bg-white p-5" data-testid={`referral-card-${t.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div data-testid="referral-clinician">
                      <div className="font-heading text-base font-semibold text-slate-900">
                        {c ? `${c.first_name} ${c.last_name}` : "—"}
                      </div>
                      <div className="text-sm text-slate-500">
                        {c ? `${c.profession}${c.specialty ? " · " + c.specialty : ""}` : ""}
                      </div>
                      {c?.suburb && (
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
                          <MapPin className="h-3 w-3" /> {c.suburb}{c.state ? ", " + c.state : ""}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 font-mono text-xs font-semibold text-blue-700" data-testid="referral-token">
                        <KeyRound className="h-3 w-3" /> #{t.token}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-slate-400" data-testid="referral-date">
                        <CalendarClock className="h-3 w-3" /> {fmtDate(t.created_at)} · {fmtTime(t.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="referral-clinic-name">
                    <Building2 className="h-3.5 w-3.5 text-slate-400" /> Referring clinic: <span className="font-medium text-slate-800">{t.gp_clinic_name}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
